import Keycloak from 'keycloak-js';
import { getLang } from './i18n';
import { isDeepLinkPath } from './deepLinks';

// Keycloak SSO (issuer https://sso.tastingnoteshub.com/realms/tastingnoteshub).
// Login is optional: anonymous users can still create sheets.
// Emergency switch-off: SSO_ENABLED = false and npm run build.
export const SSO_ENABLED = true;

const keycloak = SSO_ENABLED
  ? new Keycloak({
      url: 'https://sso.tastingnoteshub.com',
      realm: 'tastingnoteshub',
      clientId: 'tnh',
    })
  : null;

let initialized = false;

export async function initKeycloak() {
  if (!SSO_ENABLED) return null;
  if (initialized) return keycloak;
  try {
    await keycloak.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      pkceMethod: 'S256',
    });
    initialized = true;

    // Refresh the token before it expires
    setInterval(async () => {
      if (keycloak.authenticated) {
        try {
          await keycloak.updateToken(30); // refresh when it expires within 30 s
        } catch {
          keycloak.login();
        }
      }
    }, 10000);

  } catch (err) {
    console.error('Keycloak init error:', err);
  }
  return keycloak;
}

export function getToken() {
  return keycloak?.token || '';
}

export function getUser() {
  if (!SSO_ENABLED || !keycloak?.authenticated) return null;
  return {
    name: keycloak.tokenParsed?.name || keycloak.tokenParsed?.preferred_username || '',
    email: keycloak.tokenParsed?.email || '',
    firstName: keycloak.tokenParsed?.given_name || '',
    lastName: keycloak.tokenParsed?.family_name || '',
  };
}

export function logout() {
  if (!SSO_ENABLED) return;
  keycloak.logout({ redirectUri: window.location.origin });
}

export function isAuthenticated() {
  return SSO_ENABLED && (keycloak?.authenticated || false);
}

// Language of the Keycloak pages: the language currently selected in the app
export function siteLocale() {
  return getLang();
}

// A visitor who opens a sheet link and has to sign in comes back to the site root (the realm accepts it); the sheet he wanted is kept
// in the session and opened again by App.js after the login.
function rememberReturnPath() {
  try {
    if (isDeepLinkPath(window.location.pathname)) sessionStorage.setItem('tnh_return_to', window.location.pathname);
  } catch { /* storage not available */ }
}

export function login() {
  if (!SSO_ENABLED) return;
  rememberReturnPath();
  keycloak.login({ locale: siteLocale(), redirectUri: window.location.origin + '/' });
}

// Self-service registration (needs "User registration" enabled in the realm)
export function register() {
  if (!SSO_ENABLED) return;
  rememberReturnPath();
  keycloak.register({ locale: siteLocale(), redirectUri: window.location.origin + '/' });
}

// Delete the account on Keycloak (delete_account action: needs the account/delete-account role)
export function deleteSsoAccount() {
  if (!SSO_ENABLED) return;
  keycloak.login({ action: 'delete_account', locale: siteLocale() });
}

// fetch wrapper that adds the Authorization header when the user is signed in
export async function authFetch(url, options = {}) {
  // A multipart body (file upload) must keep the boundary chosen by the browser: no JSON Content-Type for FormData
  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    'Accept-Language': getLang(),   // the API answers errors in the user's language
    ...(options.headers || {}),
  };

  if (SSO_ENABLED && keycloak?.authenticated) {
    try {
      await keycloak.updateToken(5);
    } catch {
      login();
      return;
    }
    headers['Authorization'] = `Bearer ${keycloak.token}`;
  }

  return fetch(url, { ...options, headers });
}

export default keycloak;
